import Bento from "@/components/molecules/bento";
import InspirationModalManager from "@/components/organisms/inspiration-modal-manager";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { InspirationsWithProducts } from "@/types";
import { Suspense } from "react";

const variantPatterns = [
  ["lg", "sm", "md"], // First row pattern
  ["sm", "md", "lg"], // Second row pattern
];

type InspirationMasonaryGridProps = {
  locale: Locales;
  inspirations: InspirationsWithProducts[];
};

const InspirationMasonaryGrid = ({ locale, inspirations }: InspirationMasonaryGridProps) => {
  if (!inspirations || inspirations.length === 0) return null;

  return (
    <section className="container my-3 space-y-8 px-4 py-2.5 md:px-7.5 lg:my-8 lg:py-7.5">
      <h2 className="font-cabinet-grotesk text-3xl max-md:hidden">{__("The Inspirations", locale)}</h2>
      <div className="columns-1 gap-5 space-y-5 md:columns-2">
        {inspirations.map((inspiration, index) => {
          // Determine which row this item belongs to (groups of 3)
          const rowIndex = Math.floor(index / 3);
          // Get position within the row (0, 1, or 2)
          const positionInRow = index % 3;
          // Get the pattern for this row (alternating between the two patterns)
          const currentPattern = variantPatterns[rowIndex % 2];
          // Get the variant for this specific position
          const variant = currentPattern[positionInRow];

          // Skip if no featured image
          if (!inspiration.data.featured_image?.url) {
            return null;
          }

          return (
            <Bento
              key={inspiration.id}
              locale={locale}
              link={`#${inspiration.uid}`}
              creditState="default"
              variant={variant as "lg" | "sm" | "md"}
              tag={__("Discover the products", locale)}
              image={{
                src: inspiration.data.featured_image.url,
                alt: inspiration.data.featured_image.alt || `Inspiration ${inspiration.uid}`,
              }}
              sizes="(min-width: 768px) 50vw, 100vw"
              priority={index === 0}
            />
          );
        })}
        {/*  // Wrapped in <Suspense> to avoid build-time error when using `useSearchParams` inside a Client Component.
            // Without this, Next.js throws: "useSearchParams() should be wrapped in a suspense boundary"
            // See: https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
        */}
        <Suspense fallback={null}>
          <InspirationModalManager locale={locale} inspirations={inspirations} />
        </Suspense>
      </div>
    </section>
  );
};

export default InspirationMasonaryGrid;
